// ─── AutoChinBot · Background Supervisor ───────────────────────
// PROJECT-WIDE RULE: every AutoChinBot engine AND task must run in the
// background continuously — starting right after login and only stopping when
// the browser closes or the user logs out. The user NEVER has to open the
// monitor for the bot to work.
//
// This supervisor is the single source of truth that enforces that rule:
//
//   1. Run targets — the restore layer resolves WHAT should run
//      (strategy + account + symbols) and registers each as a "run target".
//
//   2. Watchdog — reconciles every few seconds:
//        • engine not running  → re-init + start   (self-heal)
//        • target running       → make sure the task pipeline (edata + etask)
//                                 is attached
//        • ANY running AutoChin engine (even one started by the monitor) is
//          guaranteed to have its task pipeline attached.
//
//   3. Logout — stops the watchdog and clears every run target. The engines
//      themselves are stopped by the restore hook.
//
// This turns the previous one-shot restore into a self-healing, always-on
// background loop, so a dropped stream or a missed attach can never silently
// kill the bot / tasks.

import {
  getOrCreateAutoChinEngine,
  getAllAutoChinEngines,
} from '@/lib/auto-chin-bot-engine';
import {
  ensureAutoChinTaskRuntime,
} from '@/lib/autochin-task-runtime';
import { buildSessionKey, parseSessionKey } from '@/lib/session-key';

// ─── Types ──────────────────────────────────────────────────────

export interface AutoChinRunTarget {
  strategyId: string;
  accountId: string;
  /** Symbols this engine streams — needed to re-init after a self-heal. */
  symbols: Array<{ symbolId: number; symbolName: string; digits?: number }>;
  timeframe: string;
}

// ─── Module-level state ─────────────────────────────────────────

const targets = new Map<string, AutoChinRunTarget>();
let supervisorUserId: string | null = null;
let watchdogTimer: ReturnType<typeof setInterval> | null = null;
let reconciling = false;

/** How often the watchdog re-checks every target + running engine. */
const WATCHDOG_MS = 15000;

// ─── Target registry ────────────────────────────────────────────

/** Upsert a run target (idempotent — safe to call on every restore attempt). */
export function upsertRunTarget(target: AutoChinRunTarget): void {
  if (!target.strategyId || !target.accountId || target.symbols.length === 0) return;
  targets.set(buildSessionKey(target.strategyId, target.accountId), target);
}

export function removeRunTarget(strategyId: string, accountId: string): void {
  targets.delete(buildSessionKey(strategyId, accountId));
}

export function getRunTargets(): AutoChinRunTarget[] {
  return [...targets.values()];
}

// ─── Supervisor lifecycle ───────────────────────────────────────

export function setAutoChinSupervisorUser(userId: string | null): void {
  supervisorUserId = userId;
}

/**
 * Start (or restart) the watchdog. Idempotent — never creates two timers.
 * Also runs one immediate reconcile so the fold starts without waiting.
 */
export function startAutoChinSupervisor(): void {
  if (!supervisorUserId) return;
  if (watchdogTimer !== null) return;

  void reconcile();
  watchdogTimer = setInterval(() => {
    void reconcile();
  }, WATCHDOG_MS);
}

function stopWatchdog(): void {
  if (watchdogTimer !== null) {
    clearInterval(watchdogTimer);
    watchdogTimer = null;
  }
}

/**
 * Full stop — used on logout. Clears targets, stops the watchdog and forgets
 * the user. Engines/runtimes are stopped by the restore hook.
 */
export function stopAutoChinSupervisor(): void {
  stopWatchdog();
  targets.clear();
  supervisorUserId = null;
}

// ─── Reconcile (the heart of the supervisor) ────────────────────

async function reconcile(): Promise<void> {
  if (!supervisorUserId || reconciling) return;
  reconciling = true;

  try {
    // 1. Managed targets — keep the engine alive + guarantee the task pipeline.
    for (const t of getRunTargets()) {
      if (!supervisorUserId) return;

      const engine = getOrCreateAutoChinEngine(t.strategyId, t.accountId);

      // Self-heal: engine died (stream dropped, tab hiccup, ...) → bring it back.
      if (!engine.isRunning) {
        try {
          await engine.init(t.accountId, t.symbols, t.timeframe);
          engine.start();
        } catch {
          /* retry on the next watchdog tick */
        }
      }

      // Guarantee the task pipeline (edata + etask) for this engine.
      ensureAutoChinTaskRuntime(t.strategyId, t.accountId, supervisorUserId);
    }

    // 2. Catch-all — ANY running AutoChin engine (e.g. started from the
    //    monitor for an account outside active_accounts) must also have its
    //    task pipeline attached, so it obeys the background rule too.
    const userId = supervisorUserId;
    if (userId) {
      for (const [key, engine] of getAllAutoChinEngines()) {
        if (!engine.isRunning) continue;
        const parsed = parseSessionKey(key);
        if (parsed) {
          ensureAutoChinTaskRuntime(parsed.strategyId, parsed.accountId, userId);
        }
      }
    }
  } finally {
    reconciling = false;
  }
}